import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasHeadingLevelGap, headings, paragraphsWithStyle, readDocument } from "@/office/word/document";

export const lektionUeberschriftenebenen: Lesson = {
  id: "ueberschriftenebenen",
  title: "Überschriftenebenen richtig verschachteln",
  summary: "Aus einzelnen Überschriften wird eine Gliederung – wenn die Ebenen stimmen.",
  durationMinutes: 9,
  learningGoals: [
    "Überschriftenebenen als Gliederung verstehen",
    "Ebenen ohne Sprünge aufbauen",
    "Die Struktur im Navigationsbereich kontrollieren",
  ],
  steps: [
    {
      id: "gliederung-aufbauen",
      title: "Eine Gliederung aufbauen",
      explanation: [
        "Überschriftenebenen sind keine Größenangaben, sondern eine Rangfolge. " +
          "„Überschrift 1“ ist ein Hauptkapitel, „Überschrift 2“ ein Unterkapitel davon, " +
          "„Überschrift 3“ ein Unterpunkt darin – genau wie die Nummerierung 1, 1.1, 1.1.1.",
        "Die häufigste Falle: Eine Ebene wird nach Aussehen gewählt („Überschrift 3 ist " +
          "schön klein“). Dadurch entsteht eine Gliederung, die inhaltlich nicht stimmt – " +
          "und das Inhaltsverzeichnis später Unsinn anzeigt.",
      ],
      keyPoints: [
        "Ebene nach Bedeutung wählen, nicht nach Schriftgröße.",
        "Das Aussehen jeder Ebene lässt sich später jederzeit anpassen.",
      ],
      task: {
        instruction:
          "Baue eine Gliederung mit genau einer „Überschrift 1“ und mindestens zwei " +
          "„Überschrift 2“ darunter. Unter jede Überschrift kommt ein Absatz Fließtext.",
        hint: "Denk an ein kleines Thema mit zwei Unterpunkten, zum Beispiel „Projektbericht“ mit „Ausgangslage“ und „Ergebnisse“.",
        check: async () => {
          const snapshot = await readDocument();
          const alle = headings(snapshot);
          if (alle.length === 0) {
            return fail(
              "Im Dokument gibt es noch keine Überschriften.",
              "Weise zuerst wie in der letzten Lektion die Vorlage „Überschrift 1“ zu.",
            );
          }

          const ebene1 = alle.filter((eintrag) => eintrag.level === 1);
          const ebene2 = alle.filter((eintrag) => eintrag.level === 2);
          const standard = paragraphsWithStyle(snapshot, "Normal");

          return fromCriteria(
            [
              criterion(
                `Genau eine Überschrift der Ebene 1 (gefunden: ${ebene1.length})`,
                ebene1.length === 1,
              ),
              criterion(
                `Mindestens zwei Überschriften der Ebene 2 (gefunden: ${ebene2.length})`,
                ebene2.length >= 2,
              ),
              criterion("Die Ebene 1 steht ganz oben", ebene1[0]?.paragraph.index === alle[0]?.paragraph.index),
              criterion(
                `Mindestens drei Fließtextabsätze (gefunden: ${standard.length})`,
                standard.length >= 3,
              ),
            ],
            {
              success: "Das ist eine saubere Gliederung.",
              failure: "Die Gliederung ist noch nicht vollständig.",
              hint: "Genau eine Überschrift 1 bedeutet: Alle weiteren Kapitelüberschriften bekommen Ebene 2.",
            },
          );
        },
      },
    },
    {
      id: "keine-spruenge",
      title: "Keine Ebene überspringen",
      explanation: [
        "Ebenen müssen lückenlos aufeinander folgen. Auf eine „Überschrift 1“ darf eine " +
          "„Überschrift 2“ folgen, aber niemals direkt eine „Überschrift 3“. Ein " +
          "übersprungener Rang bedeutet, dass ein Unterpunkt zu einem Kapitel gehört, das " +
          "es gar nicht gibt.",
        "Das ist nicht nur Formsache: Screenreader lesen die Ebenen vor, um Blinden die " +
          "Orientierung im Dokument zu ermöglichen. Eine Lücke wirkt dort wie eine fehlende " +
          "Treppenstufe.",
      ],
      keyPoints: [
        "Nach unten immer nur eine Ebene auf einmal – nach oben darf gesprungen werden.",
        "Barrierefreiheit: Screenreader nutzen genau diese Rangfolge zur Navigation.",
      ],
      task: {
        instruction:
          "Ergänze unter einer deiner „Überschrift 2“ eine „Überschrift 3“ – und achte " +
          "darauf, dass im ganzen Dokument keine Ebene übersprungen wird.",
        hint: "Cursor in die neue Zeile setzen und Strg + Alt + 3 drücken.",
        check: async () => {
          const snapshot = await readDocument();
          const alle = headings(snapshot);
          const ebene3 = alle.find((eintrag) => eintrag.level === 3);
          const vorgaenger = ebene3
            ? alle.filter((eintrag) => eintrag.paragraph.index < ebene3.paragraph.index).at(-1)
            : undefined;

          return fromCriteria(
            [
              criterion("Eine Überschrift der Ebene 3 ist vorhanden", Boolean(ebene3)),
              criterion(
                "Sie steht direkt unter einer Überschrift der Ebene 2",
                vorgaenger?.level === 2,
              ),
              criterion("Im gesamten Dokument wird keine Ebene übersprungen", !hasHeadingLevelGap(snapshot)),
            ],
            {
              success: "Die Gliederung baut lückenlos aufeinander auf.",
              failure: "Die Ebenen stimmen noch nicht.",
              hint: "Die Überschrift 3 muss unterhalb einer Überschrift 2 stehen. Steht sie direkt unter der Überschrift 1, fehlt eine Stufe.",
            },
          );
        },
      },
    },
    {
      id: "navigationsbereich",
      title: "Die Struktur im Navigationsbereich prüfen",
      explanation: [
        "Jetzt zeigt sich der erste konkrete Nutzen: Aktiviere auf der Registerkarte " +
          "„Ansicht“ das Kontrollkästchen „Navigationsbereich“. Links erscheint deine " +
          "komplette Gliederung – aber nur, weil du echte Formatvorlagen verwendet hast.",
        "Im Navigationsbereich kannst du per Klick zu jedem Kapitel springen. Noch " +
          "praktischer: Ziehst du einen Eintrag mit der Maus an eine andere Stelle, " +
          "verschiebt Word das gesamte Kapitel samt Unterkapiteln und Fließtext mit.",
        "Fehlt dort ein Kapitel oder ist es falsch eingerückt, stimmt die Ebene im " +
          "Dokument nicht. Der Navigationsbereich ist damit deine schnellste " +
          "Qualitätskontrolle.",
      ],
      keyPoints: [
        "Ansicht → Navigationsbereich zeigt die Gliederung an.",
        "Kapitel per Drag-and-drop umsortieren – der Inhalt wandert mit.",
        "Was dort nicht auftaucht, ist keine echte Überschrift.",
      ],
    },
  ],
};
